import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FileItem,
  OrderItem,
} from "../../interface/CollapsibleFolderTree";
import { POST } from "../../service/api";

// --- 1. Interfaces ---

interface OrderState {
  data: OrderItem[] | null;
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  loadingState: {
    files: boolean;
  };

  filesByLotId: Record<string, FileItem[] | null>;
  orders: OrderItem[] | null;
}

// --- 3. Slice Definition ---
const initialState: OrderState = {
  data: null,
  loading: false,
  total: 0,
  page: 1,
  pageSize: 7,
  loadingState: {
    files: false,
  },

  orders: null,
  filesByLotId: {},
};

const CustomerTestData = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setOrdersLoading: (state) => {
      state.loading = true;
    },
    setfilesLoading: (state) => {
      state.loadingState.files = true;
    },
    setFilesData: (
      state,
      action: PayloadAction<{ data: FileItem[] | null; lotId: string }>,
    ) => {
      state.loadingState.files = false;
      state.filesByLotId[action.payload.lotId] = action.payload.data;
    },
    setOrdersData: (
      state,
      action: PayloadAction<{ data: OrderItem[] | null; total: number }>,
    ) => {
      // state.loading = false;
      state.data = action.payload.data;
      state.total = action.payload.total;
    },
    setPaginatedOrders: (
      state,
      action: PayloadAction<{ data: OrderItem[] | null }>,
    ) => {
      state.loading = false;
      state.orders = action.payload.data;
    },
    setOrderPagination: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>,
    ) => {
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    setPaginationAndSlice: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>,
    ) => {
      const { page, pageSize } = action.payload;

      state.page = page;
      state.pageSize = pageSize;

      if (!state.data || state.data.length === 0) {
        state.orders = [];
        return;
      }

      const start = (page - 1) * pageSize;
      const end = start + pageSize;

      // page overflow guard
      if (start >= state.data.length) {
        state.orders = [];
        return;
      }

      state.orders = state.data.slice(start, end);
    },

    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(POST("test-data/orders", "").pending, (state, action) => {
      state.loading = true;

      state.orders = null;
      state.data = null;
      state.total = 0;

      state.filesByLotId = {};

      state.loadingState = {
        files: false,
      };
    });
    builder.addCase(POST("test-data/orders", "").fulfilled, (state, action) => {
      state.loading = false;

      const { orders } = action?.payload?.data;
      const count = orders?.length || 0;

      if (!Array.isArray(orders)) {
        state.data = [];
        state.orders = [];
        state.total = 0;
        return;
      }

      state.data = orders;
      state.total = count;

      const start = (state.page - 1) * state.pageSize;
      const end = start + state.pageSize;

      // page overflow guard
      if (start >= orders.length) {
        state.orders = [];
        return;
      }

      state.orders = orders.slice(start, end);
    });
    builder.addCase(POST("test-data/orders", "").rejected, () => initialState);

    builder.addCase(
      POST("test-data/lots/files", "").pending,
      (state, action) => {
        state.loadingState.files = true;
      },
    );
    builder.addCase(
      POST("test-data/lots/files", "").fulfilled,
      (state, action) => {
        state.loadingState.files = false;
        const { result, id } = action?.payload?.data;
        const lotId = id;
        if (!lotId) return;

        if (!Array.isArray(result)) {
          state.filesByLotId[lotId] = [];
          return;
        }

        state.filesByLotId[lotId] = result;
      },
    );
    builder.addCase(
      POST("test-data/lots/files", "").rejected,
      (state, action) => {
        state.loadingState.files = false;
      },
    );
  },
});

export const {
  setOrderPagination,

  clearState,
  setPaginationAndSlice,
} = CustomerTestData.actions;
export default CustomerTestData.reducer;
